<template>
	<div v-if="selectedPlan" class="sidebar">
        <span class="dashboard-title mb-5">fylm.ai {{ selectedPlan.name }}</span>
        <span class="dashboard-subtitle mb-4">Pricing</span>
		<div v-if="!calculatingProrate" class="price-calculation-container">
			<template v-if="proratedData.amountDue">
				<div class="item mb-2">
					<span class="name">Prorated upgrade from fylm.ai {{ currentPlan.name }} to fylm.ai {{ selectedPlan.name }}</span>
					<span class="price">{{ proratedData.amountDue.totalAmountDueDisplay }}</span>
				</div>
				<div class="item mb-2">
					<span class="name">Due today</span>
					<span class="price">{{ proratedData.amountDue.totalAmountDueDisplay }}</span>
				</div>
				<div class="item">
					<span class="name">Your bill from next payment period at {{ proratedData.amountDue.nextChargeDateDisplay }}</span>
					<span class="price">{{ proratedData.amountDue.nextChargeAmountDisplay }}</span>
				</div>
			</template>

			<template v-else>
				<div class="item mb-2">
					<span class="name">fylm.ai {{ selectedPlan.name }}</span>
					<span class="price">${{ getProductPrice(selectedPlan.slug + '-no-trial') }}</span>
				</div>

				<div class="item mb-2" v-if="getProductDiscount(selectedPlan.slug + '-no-trial') > 0">
					<span class="name">Discount</span>
					<span class="price">${{ getProductDiscount(selectedPlan.slug + '-no-trial') }}</span>
				</div>

				<div class="item mb-2">
					<span class="name">Due today</span>
					<span class="price">${{ getProductPrice(selectedPlan.slug + '-no-trial') - getProductDiscount(selectedPlan.slug + '-no-trial') }}</span>
				</div>

				<div class="item">
					<span class="name">Your bill from next payment period at {{ getNextBillingDate }}</span>
					<span class="price">${{ getProductPrice(selectedPlan.slug + '-no-trial') - getProductDiscount(selectedPlan.slug + '-no-trial') }}</span>
				</div>
			</template>

			<div class="item" v-if="additionalUsersQuantity > 0">
				<span class="name">Additional Users</span>
				<span class="price">{{ additionalUsersQuantity }} X ${{ newPlanData.additional_collaborators_cost }}</span>
			</div>

			<div class="item --total">
				<span class="name">TOTAL</span>
				<span v-if="proratedData.amountDue" class="price">{{ proratedData.amountDue.totalAmountDueDisplay }}</span>
				<span v-else class="price">${{ getProductPrice(selectedPlan.slug + '-no-trial') - getProductDiscount(selectedPlan.slug + '-no-trial') + additionalUsersPrice }}</span>
			</div>
		</div>

		<v-progress-circular v-else class="mx-auto"
							 indeterminate
							 color="var(--primary)" size="30" width="3"
		/>

        <span v-if="selectedPlan.id !== PLAN_LITE.id" class="payment-note mt-5">
            PLEASE NOTICE: It is not possible to preserve your current work when upgrading to fylm.ai Pro or Team plans. Also, it is not possible to downgrade to fylm.ai Lite once upgraded to the Pro or Team plans.
        </span>

		<TermsOfSaleAgreementCheckbox @change="termsOfSale = $event" dark />

		<v-btn :disabled="!termsOfSale && !user.terms_of_sale_agreement" @click="upgradePlan()" class="--confirm rounded-xl" large outlined>
			Subscribe
		</v-btn>
	</div>
</template>

<script>
import { mapGetters, mapState } from "vuex";
import TermsOfSaleAgreementCheckbox from "@/components/app/ui/TermsOfSaleAgreementCheckbox.vue";

export default {
	name: "ChangePlanSidebar",
	components: {TermsOfSaleAgreementCheckbox},
	data() {
		return {
			termsOfSale: false,
			calculatingProrate: false,
			proratedData: {
				amountDue: null
			}
		};
	},
	created() {
		this.$store.dispatch("Plans/setSelectedPlan", null);
		this.$store.dispatch("Organizations/fetchPendingUsers");
	},
	computed: {
		...mapState("User", ["account", "user", "currentPlan"]),
		...mapState('Plans', ['PLAN_LITE_FREE', 'PLAN_LITE', 'PLAN_FREE']),
		...mapState("Organizations", ["pendingUsers"]),
		...mapGetters("Plans", ["getPlan", "planPrice", "planData", "selectedPlan"]),
		...mapGetters("Products", ["getProductPrice", "getProductDiscount"]),

		isSubscriptionInTrial() {
			return this.currentPlan.fastspring.state == "trial" ? true : false;
		},

		getNextBillingDate() {
			return Moment().add(1, 'years').format('D/M/YYYY');
		},

		// currentPlanTotalPrice() {
		// 	return this.currentPlanPrice + this.currentPlanAdditionalUsersPrice;
		// },

		// currentPlanPrice() {
		// 	return this.planPrice(this.currentPlan.id);
		// },

		// currentPlanData() {
		// 	return this.planData(this.currentPlan.id);
		// },

		// currentPlanAdditionalUsersPrice() {
		// 	return this.additionalUsersQuantity * this.currentPlanData.additional_collaborators_cost;
		// },

		newPlanPrice() {
			return this.planPrice(this.selectedPlan.id);
		},

		newPlanData() {
			return this.planData(this.selectedPlan.id);
		},

		additionalUsersQuantity() {
			let quantity = this.currentPlan.users.added + 1 - this.newPlanData.included_collaborators - this.pendingUsers.length;
			if (quantity > 0) return quantity;
			else return 0;
		},

		additionalUsersPrice() {
			return this.additionalUsersQuantity * this.newPlanData.additional_collaborators_cost;
		},

        totalPrice() {
			return this.newPlanPrice + this.additionalUsersPrice;
		},
	},

	watch: {
		async selectedPlan() {
			// skip for free plans
			if ([this.PLAN_FREE.id, this.PLAN_LITE_FREE.id].includes(this.currentPlan.id)) {
				this.proratedData.amountDue = null;
				return;
			}

			this.calculatingProrate = true;

			const res = await this.$apiService.get(`accounts/${this.account.id}/upgrade-plan/estimate`, {
				params: {
					plan_id: this.selectedPlan.id,
				}
			});

			this.proratedData = res.data;
			this.calculatingProrate = false;
		}
	},

	methods: {
        async upgradePlan() {
            // We had a user upgrading with Paypal where no [account.id] was provided for unknown reasons.
            if (!this.account.id) {
                await this.$store.dispatch('User/fetchUser');
                if (!this.account.id) {
                    this.$snackbar('Unable to fetch account ID. Please log out and log back in. If this message appears again please <a href="https://fylm.ai/contact" style="color: white; text-decoration: underline;" target="_blank">contact us</a>.',
                        'error',
                        {
                            class: 'free-plan-used',
                            timeout: -1,
                        });

                    return;
                }
            }

            let plan_id = this.selectedPlan.id;

			this.$store.dispatch("Loader/setUpgradePlanTo", plan_id);

			if (this.account.plan_id == this.PLAN_LITE_FREE.id) {
				this.$store.dispatch("Organizations/upgradeToPaidPlan", plan_id);
				const fcsBtn = document.querySelector("#fcs-btn");

				let planSlug = this.getPlan(plan_id).slug + "-no-trial";
				if (plan_id == this.PLAN_LITE.id) planSlug = this.getPlan(plan_id).slug;

				fastspring.builder.tag({
                    action: 'PURCHASE_SUBSCRIPTION',
                    account_id: this.account.id,
					environment: process.env.NODE_ENV
				});
				fastspring.builder.recognize({
                    email: this.user.email
				});
				fcsBtn.setAttribute("data-fsc-item-path-value", planSlug);
				fcsBtn.click();
			} else {
                this.$router.push({ name: "account.billing" });
                this.$store.dispatch("Loader/onUpgradePlan");
                this.$apiService.patch(`accounts/${this.account.id}/upgrade-plan`, {
                    plan_id,
                });
			}
		},
	},
};
</script>

<style lang="scss" scoped>
.sidebar {
	padding: 50px 20px;
	height: 100%;
	display: flex;
	flex-direction: column;
	justify-content: flex-start;
	align-items: flex-start;
	background: #121212;
	color: white;

	.plan-title {
		text-transform: uppercase;
		font-size: 2rem;
		margin-bottom: 50px;
		align-self: center;
	}

	.price-calculation-container {
		width: 100%;

		.item {
			display: flex;
			justify-content: space-between;
			color: var(--font-default-color);

			&.--total {
				border-top: 1px solid white;
				margin-top: 10px;
				color: white;
				font-weight: bold;
				padding: 5px 0;
			}
		}
	}

	span {
		line-height: 1.5;
		text-align: left;
	}

	.prorate-note {
		margin-top: 25px;
	}

	.payment-note {
		color: var(--font-default-color);
	}

	.v-btn {
		margin-top: 30px !important;
		align-self: center;
	}
}
</style>
